// src/layouts/MainLayout.tsx
import React, { useState } from 'react'; // Import useState
import { Outlet, useLocation, useNavigate } from 'react-router-dom'; // Import useLocation
import { useSelector } from 'react-redux';
import { Box, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, IconButton, Avatar, useTheme, useMediaQuery } from '@mui/material'; // Import useTheme, useMediaQuery
import { Dashboard as DashboardIcon, QrCodeScanner as ScannerIcon, Inventory as LostItemsIcon, Settings as SettingsIcon, Logout as LogoutIcon, Menu as MenuIcon, GroupAdd as GroupAddIcon, ContactMail as InquiriesIcon } from '@mui/icons-material'; // Import MenuIcon, GroupAddIcon, InquiriesIcon
import { signOut } from 'firebase/auth';
import { auth } from '../services/firebase/config';
import { RootState } from '../store';

const DRAWER_WIDTH = 240;

interface MainLayoutProps {
  children?: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation(); // Get current location for selected state
  const { user, isAuthenticated, isAdmin } = useSelector((state: RootState) => state.auth);
  const theme = useTheme(); // Get theme for breakpoints
  const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // Check if screen is md or larger
  const [mobileOpen, setMobileOpen] = useState(false); // State for mobile drawer

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { text: 'Log Found Item(s)', icon: <ScannerIcon />, path: '/scanner' },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Found Item(s) History', icon: <LostItemsIcon />, path: '/lost-items' },
    // { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
    { text: 'Pre-Register Families', icon: <GroupAddIcon />, path: '/pre-registration' },
    ...(isAdmin ? [{ text: 'Inquiries', icon: <InquiriesIcon />, path: '/inquiries' }] : []),
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const drawerContent = (
    <>
      <Toolbar /> {/* Spacer */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 1, bgcolor: '#2196F3' }}>
          {user?.email?.charAt(0).toUpperCase() || 'S'}
        </Avatar>
        <Typography variant="subtitle1" fontWeight="bold">
          {isAdmin ? 'SeekTRU Admin' : (user?.email?.split('@')[0] || 'School Admin')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.email || ''}
        </Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (!isMdUp) { // Close mobile drawer on navigation
                  setMobileOpen(false);
                }
              }}
              selected={location.pathname === item.path} // Use location.pathname
              sx={{
                '&.Mui-selected': {
                  bgcolor: '#E3F2FD',
                  borderLeft: '4px solid #2196F3',
                  '&:hover': {
                    bgcolor: '#E3F2FD',
                  },
                  // Ensure icon color is also selected
                  '& .MuiListItemIcon-root': {
                     color: '#2196F3'
                  }
                }
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}> {/* Inherit color */}
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* --- AppBar --- */}
      <AppBar
        position="fixed"
        sx={{
          // Make AppBar full width on mobile, shifted on desktop when drawer is permanent
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1, // Keep AppBar above Drawer
          bgcolor: '#2196F3'
        }}
      >
        <Toolbar>
          {/* --- Hamburger Menu Button (Mobile Only) --- */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }} // Only display on screens smaller than md
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            SeekTRU
          </Typography>
          <Typography variant="subtitle2" color="inherit" sx={{ mr: 2 }}>
            School Portal
          </Typography>
          <IconButton color="inherit" onClick={handleLogout} title="Logout">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* --- Drawer --- */}
      <Box
        component="nav"
        // Conditionally set width based on screen size
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="navigation drawer"
      >
        {/* Temporary Drawer (Mobile) */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' }, // Show only on smaller screens
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, bgcolor: '#FFFFFF' },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Permanent Drawer (Desktop) */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' }, // Show only on larger screens
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, bgcolor: '#FFFFFF' },
          }}
          open // Permanent drawer is always open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* --- Main Content Area --- */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 }, // Use responsive padding
          // Adjust width and margin only needed if drawer is permanent
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          // Remove fixed marginLeft, drawer positioning handles it
          // marginLeft: `${DRAWER_WIDTH}px`,
          marginTop: '64px', // AppBar height, adjust if your AppBar height changes
          bgcolor: '#F5F5F5',
          minHeight: 'calc(100vh - 64px)',
          overflow: 'auto' // Add scroll for content overflow
        }}
      >
        {/* Main content or nested outlet */}
        {children || <Outlet />}
      </Box>
    </Box>
  );
};

export default MainLayout;